import { useState, useEffect, useCallback, useRef } from 'react'
import { toaster } from '@minetrace/ui'
import { ApiError, type PagedResponse } from '../api/client'
import { getErrorMessage } from '../api/errorCodes'

/**
 * Opciones del selector de tamano de pagina. 10 quedo corto para el directorio de mineros
 * (observacion 2 de sep 2026); 25 es el nuevo default.
 */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const
export const DEFAULT_PAGE_SIZE = 25

interface UsePagedRequestConfig<TParams> {
  /** Filter params passed to the service function. Hook re-fetches when these change. */
  params?: TParams
  /** Items per page. Es el valor INICIAL: el usuario puede cambiarlo desde el listado. */
  pageSize?: number
  /** Show error toast on failure (default: true) */
  showError?: boolean
}

interface UsePagedRequestResult<TItem> {
  items: TItem[]
  totalCount: number
  isFetching: boolean
  error: string | null
  /** Re-fetch with current params */
  refresh: () => void
  page: number
  setPage: (page: number) => void
  totalPages: number
  pageSize: number
  setPageSize: (size: number) => void
}

export function usePagedRequest<TItem, TParams extends Record<string, unknown> = Record<string, unknown>>(
  fn: (params: TParams & { page: number; pageSize: number }) => Promise<PagedResponse<TItem>>,
  config?: UsePagedRequestConfig<TParams>,
): UsePagedRequestResult<TItem> {
  const initialPageSize = config?.pageSize ?? DEFAULT_PAGE_SIZE
  const showError = config?.showError !== false
  const params = config?.params ?? ({} as TParams)
  const paramsKey = JSON.stringify(params)

  const [items, setItems] = useState<TItem[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [isFetching, setIsFetching] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPageState] = useState(1)
  const [pageSize, setPageSizeState] = useState(initialPageSize)
  const [refreshKey, setRefreshKey] = useState(0)
  const mountedRef = useRef(true)
  const fnRef = useRef(fn)
  fnRef.current = fn
  const paramsRef = useRef(params)
  paramsRef.current = params

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Reset to page 1 when params change (skip initial render)
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    setPageState(1)
  }, [paramsKey])

  const setPage = useCallback((p: number) => setPageState(p), [])

  // Cambiar el tamano tiene que volver a la pagina 1: con 10 por pagina la 8 existe,
  // con 100 no, y el usuario quedaria mirando un listado vacio.
  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size)
    setPageState(1)
  }, [])
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), [])

  useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      console.log(`[usePagedRequest] fetching page=${page} pageSize=${pageSize} params=`, paramsRef.current)
      setIsFetching(true)
      setError(null)

      try {
        const result = await fnRef.current({ ...paramsRef.current, page, pageSize })
        console.log(`[usePagedRequest] raw result:`, result)
        console.log(`[usePagedRequest] cancelled=${cancelled} mounted=${mountedRef.current}`)
        if (cancelled || !mountedRef.current) {
          console.log(`[usePagedRequest] SKIPPED setState (cancelled or unmounted)`)
          return
        }

        console.log(`[usePagedRequest] setting items (${result.items?.length ?? 'null'}), totalCount=${result.totalCount}, totalPages=${result.totalPages}`)
        setItems(result.items)
        setTotalCount(result.totalCount)
        setTotalPages(result.totalPages)
      } catch (err) {
        console.error(`[usePagedRequest] CATCH:`, err)
        if (cancelled || !mountedRef.current) return

        let message = 'Ocurrio un error inesperado'
        if (err instanceof ApiError) {
          message = getErrorMessage(err.errorCode, err.message)
        } else if (err instanceof Error) {
          message = err.message
        }

        setError(message)

        if (showError) {
          toaster.error({ description: message })
        }
      } finally {
        if (!cancelled && mountedRef.current) {
          console.log(`[usePagedRequest] setting isFetching=false`)
          setIsFetching(false)
        } else {
          console.log(`[usePagedRequest] SKIPPED isFetching=false (cancelled=${cancelled} mounted=${mountedRef.current})`)
        }
      }
    }

    fetchData()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, page, pageSize, refreshKey])

  return { items, totalCount, isFetching, error, refresh, page, setPage, totalPages, pageSize, setPageSize }
}
